import { IntegrationEnhanced, useOrgIntegrations } from "@/api/integrations";
import { EntitySelector } from "@/components/EntitySelector";
import { EntitySelectionProvider } from "@/components/EntitySelector/dnd/useEntitySelection";
import {
  RailRowSkeleton,
  useRowReservation,
} from "@/components/rail-kit/RailReveal";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { hasPlatformEntitySelector } from "@/constants/integration-platforms";
import { ROUTES } from "@/constants/routes";
import { useOpenSettings } from "@/context/OpenSettingsContext";
import type { IntegrationConnection } from "@cohesive/api-client";
import { type Entity, IntegrationIcon } from "@cohesive/ui-components";
import { AtSign, Blocks } from "lucide-react";
import { useState } from "react";
import {
  CanvasInsertMenuExpandedHeader,
  INSERT_MENU_INTEGRATION_HEIGHT,
  INSERT_MENU_INTEGRATION_WIDTH,
} from "./CanvasInsertMenu";
import { CanvasToolbarDropdown } from "./CanvasToolbarDropdown";
import { CANVAS_TOOLBAR_MENU_MIN_WIDTH } from "./canvasToolbarLayout.constants";
import { useCanvasToolbarLayout } from "./CanvasToolbarLayoutContext";

/** Rows held open while the roster is cold — the count chat's `@` menu reserves. */
const LOADING_ROWS = 3;

type MenuView =
  | { kind: "menu" }
  | { kind: "integration"; integration: IntegrationEnhanced };

interface CanvasConnectorsMenuProps {
  /** Called when the menu opens — close bespoke Type-1 flyouts. */
  onOpen?: () => void;
  onEntitySelect: (entity: Entity) => void;
}

function buildConnectionForSelector(
  integration: IntegrationEnhanced,
): IntegrationConnection | null {
  if (!integration.integration_connection_id) {
    return null;
  }
  return {
    id: integration.integration_connection_id,
    integration_id: integration.id,
    integration_name: integration.name,
    integration_platform: integration.platform,
    integration_image_url: integration.metadata?.image_url,
    integration_provider: integration.provider,
    integration_provider_id: integration.provider_id,
    organization_id: "",
    user_id: "",
    status: "active",
    beta: false,
    config: integration.integration_connection_config || {},
    instructions: "",
    created_at: integration.created_at,
    updated_at: integration.updated_at,
  };
}

interface CanvasConnectorsMenuListProps {
  /** Connected integrations that have an entity selector, already sorted. */
  integrations: IntegrationEnhanced[];
  isPending: boolean;
  /**
   * The roster failed to load. Only consulted when there is nothing to show:
   * React Query keeps serving cached `data` through a failed background
   * refetch, and a transient failure must not hide connectors the user has.
   */
  isError: boolean;
  reservedRows: number;
  skeletonVisible: boolean;
  onSelectIntegration: (integration: IntegrationEnhanced) => void;
  /** Opens connector settings; offered in both the empty and populated menu. */
  onManageConnectors: () => void;
}

/**
 * The menu body, split out so its states can be tested without driving Radix.
 *
 * A cold load used to be indistinguishable from an empty account — both
 * rendered nothing, and the empty case took the trigger down with it — so
 * users with no connectors never learned the mention path existed. Same
 * states, same copy as the composer's `ConnectorsMenuSection`.
 */
export function CanvasConnectorsMenuList({
  integrations,
  isPending,
  isError,
  reservedRows,
  skeletonVisible,
  onSelectIntegration,
  onManageConnectors,
}: CanvasConnectorsMenuListProps) {
  if (isPending) {
    return (
      <div role="status" aria-label="Loading connectors">
        {Array.from({ length: reservedRows }, (_, i) => (
          <RailRowSkeleton key={i} size="menu" visible={skeletonVisible} />
        ))}
      </div>
    );
  }

  /** Menu footer — the way out to connector settings, whatever the roster. */
  const manageConnectors = (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={onManageConnectors}>
        <Blocks className="size-4" />
        Manage connectors
      </DropdownMenuItem>
    </>
  );

  if (integrations.length === 0) {
    if (isError) {
      return (
        <p className="px-2 py-1.5 text-sm text-n-40">
          Couldn&apos;t load connectors.
        </p>
      );
    }

    return (
      <>
        <div className="px-2 pb-2.5 pt-3">
          <p className="text-sm text-n-90">No connectors to mention yet.</p>
          <p className="mt-1.5 text-xs text-n-40">
            Connectors allow you to easily add external data onto the canvas
          </p>
        </div>
        {manageConnectors}
      </>
    );
  }

  return (
    <>
      {integrations.map((integration) => (
        <DropdownMenuItem
          key={integration.id}
          onSelect={(event) => {
            event.preventDefault();
            onSelectIntegration(integration);
          }}
        >
          <IntegrationIcon
            platform={integration.platform}
            name={integration.name}
            imageURL={integration.metadata?.image_url}
            className="size-4 rounded"
          />
          {integration.name}
        </DropdownMenuItem>
      ))}
      {manageConnectors}
    </>
  );
}

/**
 * Connectors — the external-integrations entry on the primary bar (design
 * system ruling: promoted out of the Insert menu to its own top-level
 * trigger; Insert keeps its copy). Same entries, same entity-selector flow.
 */
export function CanvasConnectorsMenu({
  onOpen,
  onEntitySelect,
}: CanvasConnectorsMenuProps) {
  const { tooltipSide, menuSide, submenuGapPx } = useCanvasToolbarLayout();
  const { data: integrations = [], isPending, isError } = useOrgIntegrations();
  const openSettings = useOpenSettings();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<MenuView>({ kind: "menu" });
  const { reservedRows, skeletonVisible } = useRowReservation({
    active: open,
    pending: isPending,
    fallbackRows: LOADING_ROWS,
  });

  const connectedIntegrations = integrations
    .filter(
      (i) =>
        i.connected &&
        i.integration_connection_id &&
        hasPlatformEntitySelector(i.platform),
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const closeMenu = () => {
    setOpen(false);
    setView({ kind: "menu" });
  };

  const integrationView =
    view.kind === "integration"
      ? (() => {
          const connection = buildConnectionForSelector(view.integration);
          if (!connection) return null;
          return (
            <div
              className="flex flex-col overflow-hidden rounded-lg border border-n-10 bg-n-00 shadow-elev-5"
              style={{
                width: INSERT_MENU_INTEGRATION_WIDTH,
                height: INSERT_MENU_INTEGRATION_HEIGHT,
              }}
            >
              <CanvasInsertMenuExpandedHeader
                icon={
                  <IntegrationIcon
                    platform={view.integration.platform}
                    name={view.integration.name}
                    imageURL={view.integration.metadata?.image_url}
                    className="size-4 rounded"
                  />
                }
                label={view.integration.name}
                onBack={() => setView({ kind: "menu" })}
              />
              <div className="min-h-0 flex-1">
                <EntitySelectionProvider key={connection.id}>
                  <EntitySelector
                    key={connection.id}
                    connection={connection}
                    onClose={closeMenu}
                    onSelect={(entity) => {
                      onEntitySelect(entity);
                      closeMenu();
                    }}
                    className="flex-1 border-0"
                  />
                </EntitySelectionProvider>
              </div>
            </div>
          );
        })()
      : null;

  return (
    <CanvasToolbarDropdown
      label="Connectors"
      tooltipSide={tooltipSide}
      icon={<AtSign className="size-4" strokeWidth={2} />}
      showCaret={false}
      side={menuSide}
      align="center"
      sideOffset={submenuGapPx}
      minWidth={
        view.kind === "integration"
          ? INSERT_MENU_INTEGRATION_WIDTH
          : CANVAS_TOOLBAR_MENU_MIN_WIDTH
      }
      contentClassName={
        view.kind === "integration"
          ? "border-0 bg-transparent p-0 shadow-none"
          : undefined
      }
      onOpen={onOpen}
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setView({ kind: "menu" });
        }
      }}
    >
      {view.kind === "integration" ? (
        integrationView
      ) : (
        <CanvasConnectorsMenuList
          integrations={connectedIntegrations}
          isPending={isPending}
          isError={isError}
          reservedRows={reservedRows}
          skeletonVisible={skeletonVisible}
          onSelectIntegration={(integration) =>
            setView({ kind: "integration", integration })
          }
          onManageConnectors={() => openSettings(ROUTES.SETTINGS.INTEGRATIONS)}
        />
      )}
    </CanvasToolbarDropdown>
  );
}
